import { useEffect, useRef } from 'react';

import clsxm from '@/lib/clsxm';

/**
 * The brand panel's background: a field of neurons drawn on a canvas. Cells
 * drift on slow paths in three depth layers: the far one is out-of-focus
 * light, the middle and near ones link to nearby cells of their layer with
 * faint curved lines, and the large cells have branching dendrites. Signals
 * run along the links as bright streaks, lighting each cell they reach and
 * sometimes travelling on. The cursor lights the cells near it and nudges the
 * field. With `animate` off (reduced motion) it draws one still frame.
 * Nothing is downloaded; it runs only while mounted.
 */

// Colours as "r, g, b" for rgba().
const LINK = '110, 231, 183'; // emerald-300
const CORE = '236, 253, 245'; // emerald-50
// Far, out-of-focus cells: emerald and teal.
const BOKEH_COLOURS = ['110, 231, 183', '94, 234, 212'];
// Signals: mostly emerald, some cyan and lime.
const SIGNAL_COLOURS = ['167, 243, 208', '103, 232, 249', '190, 242, 100'];

const MAX_CELLS = 170;
// Square px of panel per cell, and the cap on canvas pixels per CSS pixel.
const AREA_PER_CELL = 5500;
const MAX_DPR = 1.5;
// Cells nearer than this link; far cells are only light.
const LINKED_DEPTH = 0.5;

type Dendrite = {
  angle: number;
  length: number;
  bend: number;
  /** Angle of the side branch from its middle; 0 for none. */
  fork: number;
};

type Cell = {
  /** Resting place, as a fraction of the panel. */
  nx: number;
  ny: number;
  /** Wander: amplitude (px), speed (rad/s) and phase on each axis. */
  ax: number;
  ay: number;
  fx: number;
  fy: number;
  px: number;
  py: number;
  /** Depth: 0.3 far to 1 near. Near cells are bigger, brighter, move more. */
  z: number;
  r: number;
  /** Only the large cells have dendrites. */
  dendrites: Dendrite[];
  /** Far cells: colour and twinkle phase. */
  tint: number;
  twinkle: number;
  x: number;
  y: number;
  /** When a signal last reached it, in seconds. */
  fired: number;
};

type Signal = {
  from: number;
  to: number;
  start: number;
  duration: number;
  colour: number;
};

/** Small seeded PRNG (mulberry32), so the field has the same shape on every visit. */
function seeded(seed: number) {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeCells(): Cell[] {
  const rand = seeded(20261023);
  const gauss = () =>
    Math.sqrt(-2 * Math.log(Math.max(rand(), 1e-6))) *
    Math.cos(2 * Math.PI * rand());
  return Array.from({ length: MAX_CELLS }, () => {
    const layer = rand();
    const z =
      layer < 0.3
        ? 0.3 + rand() * 0.18
        : layer < 0.75
          ? 0.55 + rand() * 0.2
          : 0.8 + rand() * 0.2;
    // Most cells gather in the upper middle, above the copy.
    const clustered = rand() < 0.58;
    const large = z > 0.72 && rand() < 0.3;
    return {
      nx: clustered ? 0.6 + gauss() * 0.24 : rand() * 1.1 - 0.05,
      ny: clustered ? 0.3 + gauss() * 0.17 : rand() * 0.95 - 0.05,
      ax: (6 + rand() * 18) * z,
      ay: (6 + rand() * 14) * z,
      fx: 0.08 + rand() * 0.17,
      fy: 0.08 + rand() * 0.17,
      px: rand() * Math.PI * 2,
      py: rand() * Math.PI * 2,
      z,
      r: large ? 2.4 + rand() * 1.3 : 0.6 + z * 1.4,
      dendrites: large
        ? Array.from({ length: 4 + Math.floor(rand() * 3) }, () => ({
            angle: rand() * Math.PI * 2,
            length: 14 + rand() * 22,
            bend: (rand() - 0.5) * 1.2,
            fork:
              rand() < 0.55
                ? (rand() < 0.5 ? -1 : 1) * (0.5 + rand() * 0.4)
                : 0,
          }))
        : [],
      tint: rand() < 0.6 ? 0 : 1,
      twinkle: rand() * Math.PI * 2,
      x: 0,
      y: 0,
      fired: -Infinity,
    };
  });
}

/** A soft round glow, drawn once and stamped for every cell and signal. */
function glowSprite(rgb: string) {
  const sprite = document.createElement('canvas');
  sprite.width = sprite.height = 64;
  const ctx = sprite.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.12, `rgba(${rgb}, 0.9)`);
    gradient.addColorStop(0.36, `rgba(${rgb}, 0.25)`);
    gradient.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
  }
  return sprite;
}

/** An out-of-focus disc: flat in the middle, soft at the rim. */
function bokehSprite(rgb: string) {
  const sprite = document.createElement('canvas');
  sprite.width = sprite.height = 64;
  const ctx = sprite.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, `rgba(${rgb}, 0.6)`);
    gradient.addColorStop(0.62, `rgba(${rgb}, 0.42)`);
    gradient.addColorStop(0.86, `rgba(${rgb}, 0.14)`);
    gradient.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
  }
  return sprite;
}

/** Fixed sideways bend of the link between cells i < j, -0.5 to 0.5. */
function bendOf(i: number, j: number) {
  const s = Math.sin(i * 12.9898 + j * 78.233) * 43758.5453;
  return s - Math.floor(s) - 0.5;
}

function startField(canvas: HTMLCanvasElement, animate: boolean) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => undefined;

  const cells = makeCells();
  const cellGlow = glowSprite(LINK);
  const bokeh = BOKEH_COLOURS.map(bokehSprite);
  const signalGlow = SIGNAL_COLOURS.map(glowSprite);
  let width = 0;
  let height = 0;
  let count = 0;
  let reach = 0;
  let neighbours: number[][] = [];
  let signals: Signal[] = [];
  let nextSignal = 0;
  let nextPointerSignal = 0;
  let frame = 0;
  let last = 0;
  let shown = false;
  // Parallax offset (eased toward the cursor) and the cursor on the panel.
  const pointer = { x: 0, y: 0, over: false, tx: 0, ty: 0, ox: 0, oy: 0 };

  // Fades everything out toward the copy at the bottom of the panel.
  const fadeAt = (y: number) =>
    y < height * 0.48
      ? 1
      : Math.max(0.12, 1 - (y - height * 0.48) / (height * 0.42));

  const curve = (i: number, j: number) => {
    const a = cells[i];
    const b = cells[j];
    const k = bendOf(i, j) * 0.35;
    return {
      a,
      b,
      cx: (a.x + b.x) / 2 - (b.y - a.y) * k,
      cy: (a.y + b.y) / 2 + (b.x - a.x) * k,
    };
  };

  const send = (from: number, to: number, time: number, colour: number) => {
    const a = cells[from];
    const b = cells[to];
    const distance = Math.hypot(b.x - a.x, b.y - a.y);
    signals.push({
      from,
      to,
      start: time,
      duration: Math.min(1.6, Math.max(0.45, distance / (110 + 70 * a.z))),
      colour,
    });
  };

  const drawDendrites = (cell: Cell, time: number, alpha: number) => {
    ctx.strokeStyle = `rgba(${LINK}, ${alpha.toFixed(3)})`;
    ctx.fillStyle = `rgba(${LINK}, ${Math.min(1, alpha * 1.6).toFixed(3)})`;
    for (const dendrite of cell.dendrites) {
      const angle =
        dendrite.angle + Math.sin(time * 0.35 + dendrite.angle * 3) * 0.08;
      const ex = cell.x + Math.cos(angle) * dendrite.length;
      const ey = cell.y + Math.sin(angle) * dendrite.length;
      const cx =
        cell.x + Math.cos(angle + dendrite.bend) * dendrite.length * 0.55;
      const cy =
        cell.y + Math.sin(angle + dendrite.bend) * dendrite.length * 0.55;
      // Split at the middle so the dendrite tapers: thick, then thin.
      const mx = (cell.x + 2 * cx + ex) / 4;
      const my = (cell.y + 2 * cy + ey) / 4;
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(cell.x, cell.y);
      ctx.quadraticCurveTo((cell.x + cx) / 2, (cell.y + cy) / 2, mx, my);
      ctx.stroke();
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(mx, my);
      ctx.quadraticCurveTo((cx + ex) / 2, (cy + ey) / 2, ex, ey);
      if (dendrite.fork) {
        const forkAngle = angle + dendrite.fork;
        ctx.moveTo(mx, my);
        ctx.lineTo(
          mx + Math.cos(forkAngle) * dendrite.length * 0.42,
          my + Math.sin(forkAngle) * dendrite.length * 0.42,
        );
      }
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(ex, ey, 0.8, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const draw = (time: number) => {
    // Nothing to draw until the first resize has measured the panel.
    if (!count) return;
    ctx.clearRect(0, 0, width, height);
    pointer.ox += (pointer.tx - pointer.ox) * 0.04;
    pointer.oy += (pointer.ty - pointer.oy) * 0.04;

    for (let i = 0; i < count; i++) {
      const cell = cells[i];
      cell.x =
        cell.nx * width +
        Math.sin(time * cell.fx + cell.px) * cell.ax +
        pointer.ox * cell.z;
      cell.y =
        cell.ny * height +
        Math.cos(time * cell.fy + cell.py) * cell.ay +
        pointer.oy * cell.z;
    }

    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';

    // The far layer: out-of-focus light that twinkles slowly.
    for (let i = 0; i < count; i++) {
      const cell = cells[i];
      if (cell.z >= LINKED_DEPTH) continue;
      const size = 20 + (LINKED_DEPTH - cell.z) * 130;
      ctx.globalAlpha =
        (0.14 + 0.12 * (0.5 + 0.5 * Math.sin(time * 0.5 + cell.twinkle))) *
        fadeAt(cell.y);
      ctx.drawImage(
        bokeh[cell.tint],
        cell.x - size / 2,
        cell.y - size / 2,
        size,
        size,
      );
    }
    ctx.globalAlpha = 1;

    // Links between nearby cells of the same layer.
    neighbours = Array.from({ length: count }, () => []);
    for (let i = 0; i < count; i++) {
      const a = cells[i];
      if (a.z < LINKED_DEPTH) continue;
      for (let j = i + 1; j < count; j++) {
        const b = cells[j];
        if (b.z < LINKED_DEPTH || Math.abs(a.z - b.z) > 0.26) continue;
        const depth = (a.z + b.z) / 2;
        const limit = reach * (0.55 + 0.6 * depth);
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        if (Math.abs(dx) > limit || Math.abs(dy) > limit) continue;
        const distance = Math.hypot(dx, dy);
        if (distance > limit) continue;
        neighbours[i].push(j);
        neighbours[j].push(i);
        const strength = 1 - distance / limit;
        const alpha =
          strength * strength * (0.14 + 0.5 * depth) * fadeAt((a.y + b.y) / 2);
        const { cx, cy } = curve(i, j);
        ctx.strokeStyle = `rgba(${LINK}, ${alpha.toFixed(3)})`;
        ctx.lineWidth = 0.4 + 0.75 * depth;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(cx, cy, b.x, b.y);
        ctx.stroke();
      }
    }

    // The cursor joins the network: faint links to the cells near it.
    if (pointer.over) {
      for (let i = 0; i < count; i++) {
        const cell = cells[i];
        if (cell.z < LINKED_DEPTH) continue;
        const distance = Math.hypot(cell.x - pointer.x, cell.y - pointer.y);
        if (distance > 130) continue;
        const strength = 1 - distance / 130;
        ctx.strokeStyle = `rgba(${LINK}, ${(0.35 * strength * strength).toFixed(3)})`;
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(pointer.x, pointer.y);
        ctx.lineTo(cell.x, cell.y);
        ctx.stroke();
      }
    }

    // Linked cells: dendrites, halo, core, and a ring when a signal arrives.
    for (let i = 0; i < count; i++) {
      const cell = cells[i];
      if (cell.z < LINKED_DEPTH) continue;
      const fade = fadeAt(cell.y);
      const since = time - cell.fired;
      const flash = since >= 0 && since < 1.2 ? 1 - since / 1.2 : 0;
      let near = 0;
      if (pointer.over) {
        const distance = Math.hypot(cell.x - pointer.x, cell.y - pointer.y);
        if (distance < 150) near = (1 - distance / 150) ** 2;
      }
      const large = cell.dendrites.length > 0;

      if (large) {
        drawDendrites(cell, time, (0.26 + 0.34 * flash) * cell.z * fade);
      }

      const size = (large ? 46 : 12) + 16 * cell.z + 26 * flash + 10 * near;
      ctx.globalAlpha = Math.min(
        1,
        (0.12 + 0.42 * cell.z + 0.6 * flash + 0.5 * near) * fade,
      );
      ctx.drawImage(cellGlow, cell.x - size / 2, cell.y - size / 2, size, size);

      ctx.globalAlpha = Math.min(1, (0.45 + 0.5 * cell.z + flash) * fade);
      ctx.fillStyle = `rgb(${CORE})`;
      ctx.beginPath();
      ctx.arc(cell.x, cell.y, cell.r, 0, Math.PI * 2);
      ctx.fill();

      if (flash > 0) {
        const grown = 1 - flash ** 3;
        ctx.globalAlpha = 0.5 * flash * flash * fade;
        ctx.strokeStyle = `rgb(${LINK})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cell.x, cell.y, cell.r + 3 + 20 * grown, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    if (!animate) return;

    // New signals: from a random linked cell, and from the cell nearest the
    // cursor while it rests on the panel.
    const maxSignals = Math.round(count / 6);
    if (time >= nextSignal && signals.length < maxSignals) {
      const from = Math.floor(Math.random() * count);
      const onward = neighbours[from];
      if (onward.length) {
        const roll = Math.random();
        send(
          from,
          onward[Math.floor(Math.random() * onward.length)],
          time,
          roll < 0.75 ? 0 : roll < 0.9 ? 1 : 2,
        );
      }
      nextSignal = time + 0.25 + Math.random() * 0.5;
    }
    if (pointer.over && time >= nextPointerSignal) {
      let nearest = -1;
      let best = 160;
      for (let i = 0; i < count; i++) {
        const distance = Math.hypot(
          cells[i].x - pointer.x,
          cells[i].y - pointer.y,
        );
        if (distance < best && neighbours[i].length) {
          best = distance;
          nearest = i;
        }
      }
      if (nearest >= 0) {
        const onward = neighbours[nearest];
        cells[nearest].fired = time;
        send(
          nearest,
          onward[Math.floor(Math.random() * onward.length)],
          time,
          0,
        );
      }
      nextPointerSignal = time + 0.7;
    }

    // Move signals; on arrival the cell fires and may pass it on.
    const moving: Signal[] = [];
    const born: Signal[] = [];
    const previous = signals;
    signals = born;
    for (const signal of previous) {
      const progress = (time - signal.start) / signal.duration;
      if (progress >= 1) {
        cells[signal.to].fired = time;
        const onward = neighbours[signal.to].filter((j) => j !== signal.from);
        const roll = Math.random();
        const branches = !onward.length
          ? 0
          : roll < 0.18
            ? 2
            : roll < 0.62
              ? 1
              : 0;
        for (
          let k = 0;
          k < branches && moving.length + born.length < maxSignals;
          k++
        ) {
          send(
            signal.to,
            onward[Math.floor(Math.random() * onward.length)],
            time,
            signal.colour,
          );
        }
        continue;
      }
      moving.push(signal);

      // A bright streak eased along the link's curve: a thin tail that
      // thickens and brightens toward the head.
      const p = progress * progress * (3 - 2 * progress);
      const low = Math.min(signal.from, signal.to);
      const { a, b, cx, cy } = curve(low, Math.max(signal.from, signal.to));
      const at = (t: number) => {
        const u = signal.from === low ? t : 1 - t;
        const m = 1 - u;
        return [
          m * m * a.x + 2 * m * u * cx + u * u * b.x,
          m * m * a.y + 2 * m * u * cy + u * u * b.y,
        ];
      };
      const rgb = SIGNAL_COLOURS[signal.colour];
      const tail = 0.38;
      let [x0, y0] = at(Math.max(0, p - tail));
      for (let k = 1; k <= 8; k++) {
        const [x1, y1] = at(Math.max(0, p - tail + (tail * k) / 8));
        const weight = k / 8;
        ctx.strokeStyle = `rgba(${rgb}, ${(0.9 * weight * weight * fadeAt(y1)).toFixed(3)})`;
        ctx.lineWidth = 0.5 + 1.7 * weight;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
        x0 = x1;
        y0 = y1;
      }
      const fade = fadeAt(y0);
      ctx.globalAlpha = fade;
      ctx.drawImage(signalGlow[signal.colour], x0 - 11, y0 - 11, 22, 22);
      ctx.fillStyle = 'rgb(255, 255, 255)';
      ctx.beginPath();
      ctx.arc(x0, y0, 1.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    signals = moving.concat(born);
  };

  const reveal = () => {
    if (shown) return;
    shown = true;
    canvas.style.opacity = '1';
  };

  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    width = rect.width;
    height = rect.height;
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    count = Math.min(
      MAX_CELLS,
      Math.max(56, Math.round((width * height) / AREA_PER_CELL)),
    );
    reach = Math.min(180, Math.max(100, Math.min(width, height) * 0.21));
    signals = signals.filter(
      (signal) => signal.from < count && signal.to < count,
    );
    if (!animate) {
      // A still frame: a few large cells caught just after firing.
      cells.forEach((cell, i) => {
        if (cell.dendrites.length && i % 2 === 0) cell.fired = -0.45;
      });
      draw(0);
      reveal();
    }
  };

  const loop = (now: number) => {
    frame = requestAnimationFrame(loop);
    // At most ~60 frames a second, also on 120 Hz screens; none until the
    // panel has been measured.
    if (!count || now - last < 15) return;
    last = now;
    draw(now / 1000);
    reveal();
  };

  const onPointer = (event: PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    pointer.x = event.clientX - rect.left;
    pointer.y = event.clientY - rect.top;
    pointer.over =
      pointer.x >= 0 &&
      pointer.y >= 0 &&
      pointer.x <= rect.width &&
      pointer.y <= rect.height;
    pointer.tx = (0.5 - event.clientX / window.innerWidth) * 18;
    pointer.ty = (0.5 - event.clientY / window.innerHeight) * 12;
  };
  const onLeave = () => {
    pointer.over = false;
  };

  const observer = new ResizeObserver(resize);
  observer.observe(canvas);
  if (animate) {
    window.addEventListener('pointermove', onPointer, { passive: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    frame = requestAnimationFrame(loop);
  }

  return () => {
    observer.disconnect();
    cancelAnimationFrame(frame);
    window.removeEventListener('pointermove', onPointer);
    document.documentElement.removeEventListener('pointerleave', onLeave);
  };
}

export default function NeuralField({
  animate,
  className,
}: {
  animate: boolean;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const swellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    return startField(canvas, animate);
  }, [animate]);

  // A slow swell of light behind the cluster (compositor-only).
  useEffect(() => {
    const light = swellRef.current;
    if (!animate || !light?.animate) return;
    const swell = light.animate(
      [
        { opacity: 0.45, transform: 'scale(1)' },
        { opacity: 1, transform: 'scale(1.08)' },
      ],
      {
        duration: 9000,
        direction: 'alternate',
        iterations: Infinity,
        easing: 'ease-in-out',
      },
    );
    return () => swell.cancel();
  }, [animate]);

  return (
    <div
      aria-hidden='true'
      className={clsxm('absolute inset-0 overflow-hidden', className)}
    >
      <div
        ref={swellRef}
        className='absolute -right-[25%] -top-[20%] h-[80%] w-[110%] rounded-full opacity-70'
        style={{
          backgroundImage:
            'radial-gradient(closest-side, rgba(16, 185, 129, 0.22), rgba(16, 185, 129, 0.06) 55%, transparent)',
        }}
      />
      <canvas
        ref={canvasRef}
        className='absolute inset-0 h-full w-full opacity-0 transition-opacity duration-1000'
      />
    </div>
  );
}
